import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  Menu,
  MenuItem,
} from '@mui/material';
import { lessonsAPI } from '../services/api';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import QuizIcon from '@mui/icons-material/Quiz';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import LessonDetail from '../components/lessons/LessonDetail';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import WorkIcon from '@mui/icons-material/Work';

// Допоміжна функція для створення уроку з даних запиту
const createLessonFromRequest = (requestData) => {
  const defaults = {
    title: '',
    contentType: 'standard',
    content: '',
    duration: 1,
    type: 'text',
    videoUrl: '',
    contentBlocks: [],
    resources: [],
    practiceExercises: [],
    isDraft: false
  };

  return {
    ...defaults,
    ...requestData.lesson,
    ...requestData.proposedChanges
  };
};

const LessonPage = () => {
  const { lessonSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Получаем requestId из query параметров
  const searchParams = new URLSearchParams(location.search);
  const requestId = searchParams.get('requestId');
  const { user } = useAuth();
  const { t } = useTranslation(['lessons', 'common', 'tests']);
  const { currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [isEditingFromRequest, setIsEditingFromRequest] = useState(false);
  const [activeChangeRequest, setActiveChangeRequest] = useState(null);
  const [materialsModalOpen, setMaterialsModalOpen] = useState(false);
  const [testMenuAnchor, setTestMenuAnchor] = useState(null);
  const [testMenuItems, setTestMenuItems] = useState([]);

  // Проверяем, является ли пользователь админом или учителем
  const isAdmin = user?.userMeta?.roles?.includes('admin');
  const isTeacher = user?.userMeta?.roles?.includes('teacher');
  const hasEditRights = isAdmin || isTeacher || (lesson?.instructor?._id === user?._id);

  // Загрузка данных урока
  useEffect(() => {
    
    const fetchDataRequest = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsEditingFromRequest(true);

        const response = await lessonsAPI.getMyChangeRequestById(requestId);
        
        if (response && response.data) {
          const requestData = response.data.data;
          // Создаем урок с данными из запроса
          const lessonFromRequest = createLessonFromRequest(requestData);
          setLesson(lessonFromRequest);
        } else {
          setError(t('lessons:page.errors.requestNotFound'));
        }
      } catch (err) {
        console.error('Error loading change request:', err);
        setError(
          err.response?.data?.message || t('lessons:page.errors.requestLoadError')
        );
      } finally {
        setLoading(false);
      }
    };

    const fetchLesson = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsEditingFromRequest(false);

        const response = await lessonsAPI.getById(lessonSlug);
        
        if (response && response.data) {
          setLesson(response.data.data);
        } else {
          setError(t('lessons:page.errors.notFound'));
        }
      } catch (err) {
        console.error('Error loading lesson:', err);
        setError(
          err.response?.data?.message || t('lessons:page.errors.loadError')
        );
      } finally {
        setLoading(false);
      }
    };

    // Если есть requestId, загружаем данные из запроса
    if (requestId) {
      fetchDataRequest();
    } else {
      // Иначе загружаем обычный урок
    fetchLesson();
    }
  }, [lessonSlug, requestId, t, location.search]);

  // Загрузка курсов, в которых используется урок
  useEffect(() => {
    const fetchCourses = async () => {
      if (!lesson) return;
      
      try {
        setCoursesLoading(true);
        const response = await lessonsAPI.getCourses(lesson._id);
        
        if (response && response.data) {
          setCourses(response.data.data);
        }
      } catch (err) {
        console.error('Error loading courses:', err);
        toast.error(t('lessons:page.errors.coursesLoadError'));
      } finally {
        setCoursesLoading(false);
      }
    };

    const fetchModules = async () => {
      if (!lesson) return;
      
      try {
        setModulesLoading(true);
        const response = await lessonsAPI.getModules(lesson._id);
        
        if (response && response.data) {
          setModules(response.data.data);
        }
      } catch (err) {
        console.error('Error loading modules:', err);
        toast.error('Ошибка при загрузке модулей');
      } finally {
        setModulesLoading(false);
      }
    };

    const fetchActiveChangeRequest = async () => {
      if (!lesson || !user || hasEditRights) return;
      try {
        const response = await lessonsAPI.getUserChangeRequests({ status: 'pending', limit: 100 });
        const requests = response?.data?.data || [];
        const found = requests.find(r => {
          const lessonId = r.lesson?._id || r.lesson;
          return lessonId?.toString() === lesson._id?.toString();
        });
        setActiveChangeRequest(found || null);
      } catch {
        // silent — not critical
      }
    };

    fetchCourses();
    fetchModules();
    fetchActiveChangeRequest();
  }, [lessonSlug, lesson, user, hasEditRights, t]);

  // Обработчик для возврата к библиотеке уроков
  const handleBackToLibrary = () => {
    // Check if user came from forum
    if (location.state?.from === 'forum' || location.state?.fromForum) {
      navigate('/forum');
    } else {
      navigate('/lessons');
    }
  };

  // Обработчик для редактирования урока
  const handleEditLesson = () => {
    if (isEditingFromRequest && requestId) {
      // Если редактируем из запроса, передаем requestId
      navigate(`/lessons/edit/${lessonSlug}?requestId=${requestId}`);
    } else {
      // Обычное редактирование урока
    navigate(`/lessons/edit/${lessonSlug}`);
    }
  };

  // Обработчик переключения закладки урока
  const handleToggleBookmark = async () => {
    if (!user) {
      toast.info(t('lessons:page.messages.loginToBookmark'));
      return;
    }

    if (!lesson) return;

    try {
      await lessonsAPI.toggleBookmark(lesson._id);
      const newBookmarkedState = !lesson.bookmarked;
      
      // Обновляем состояние урока
      setLesson(prev => ({
        ...prev,
        bookmarked: newBookmarkedState,
      }));

      toast.success(
        newBookmarkedState
          ? t('lessons:page.messages.bookmarkAdded')
          : t('lessons:page.messages.bookmarkRemoved')
      );
    } catch (error) {
      console.error('Ошибка при управлении закладкой урока:', error);
      toast.error(t('lessons:page.messages.bookmarkError'));
    }
  };

  // Обработчик для открытия материалов
  const handleOpenMaterials = () => {
    setMaterialsModalOpen(true);
  };

  // Обработчик для закрытия материалов
  const handleCloseMaterials = () => {
    setMaterialsModalOpen(false);
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = currentLanguage === 'ru' ? 'ru-RU' : 'en-US';
    
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!lesson) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">{t('lessons:page.errors.notFound')}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      {/* Верхняя панель с кнопками */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
        <Typography variant="h4" component="h1">
          {lesson.title}
        </Typography>
          {isEditingFromRequest && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              {t('lessons:page.editingFromRequest')}
            </Typography>
          )}
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToLibrary}
            sx={{ 
              mr: 2,
              fontSize: '0.8rem',
              textTransform: 'none',
            }}
          >
            {t('lessons:page.buttons.back')}
          </Button>
          {isAdmin && lesson.tests && lesson.tests.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<QuizIcon />}
              onClick={(e) => {
                if (lesson.tests.length === 1) {
                  navigate(`/admin/tests/${lesson.tests[0]._id || lesson.tests[0]}`);
                } else {
                  setTestMenuItems(lesson.tests);
                  setTestMenuAnchor(e.currentTarget);
                }
              }}
              sx={{ mr: 2 }}
            >
              {t('tests:view.tests')} ({lesson.tests.length})
            </Button>
          )}
          {hasEditRights && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditLesson}
            >
              {t('lessons:page.buttons.edit')}
            </Button>
          )}
          {user && !hasEditRights && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                if (activeChangeRequest) {
                  navigate(`/request-lesson-changes/${lessonSlug}?requestId=${activeChangeRequest._id}`);
                } else {
                  navigate(`/request-lesson-changes/${lessonSlug}`);
                }
              }}
            >
              {activeChangeRequest
                ? t('lessonDetailPage.editExistingRequest')
                : t('lessonDetailPage.suggestChanges')
              }
            </Button>
          )}
        </Box>
      </Box>

      {/* Информация об уроке */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={lesson.isDraft ? t('lessons:page.status.draft') : t('lessons:page.status.published')} 
                color={lesson.isDraft ? 'warning' : 'success'}
                sx={{ mr: 1 }}
              />
              <Chip 
                label={t(`lessons:types.${lesson.type}`)} 
                color="primary"
                sx={{ mr: 1 }}
              />
              <Chip 
                label={lesson.contentType === 'mixed' ? t('lessons:contentTypes.mixed') : t(`lessons:contentTypes.${lesson.contentType}.name`)} 
                color="info"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                {lesson.duration} {t('lessons:page.minutes')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                {lesson.instructor?.username || t('lessons:page.unknownAuthor')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                {t('lessons:page.created')}: {formatDate(lesson.createdAt)}
                {lesson.updatedAt && lesson.updatedAt !== lesson.createdAt && 
                  `, ${t('lessons:page.updated')}: ${formatDate(lesson.updatedAt)}`}
              </Typography>
            </Box>

            {/* Профессии */}
            {lesson.professions && lesson.professions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('lessons:page.professions')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {lesson.professions.map((profession, index) => {
                    // Извлекаем название профессии из разных возможных структур
                    const professionName = profession.term_group?.MainTerm?.value || 
                                         profession.term_group?.name || 
                                         profession.name || 
                                         profession.title || 
                                         'Неизвестная профессия';
                    
                    return (
                      <Chip
                        key={profession.id || profession._id || index}
                        icon={<WorkIcon />}
                        label={professionName}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{
                          fontSize: '0.75rem',
                          height: '24px',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                          '& .MuiChip-icon': {
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('lessons:page.usedInCourses')}
            </Typography>
            
            {coursesLoading ? (
              <CircularProgress size={24} />
            ) : courses.length > 0 ? (
              <Box>
                {courses.map((course) => (
                  <Box 
                    key={course._id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/courses/${course.slug || course._id}`)}
                  >
                    <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">{course.title}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('lessons:page.notUsedInCourses')}
              </Typography>
            )}
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('lessons:page.modulesContainingLesson')}
            </Typography>
            
            {modulesLoading ? (
              <CircularProgress size={24} />
            ) : modules.length > 0 ? (
              <Box>
                {modules.map((module) => (
                  <Box 
                    key={module._id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/modules/${module.slug || module._id}`)}
                  >
                    <MenuBookIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">{module.title}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('lessons:page.notUsedInModules')}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Unified Lesson Detail Component */}
      {lesson && (
        <LessonDetail
          lesson={lesson}
          courseData={null} // Для standalone урока нет данных курса
          currentModule={null} // Для standalone урока нет модуля
          moduleIndex={null}
          lessonIndex={null}
          isBookmarked={lesson.bookmarked || false}
          onToggleBookmark={handleToggleBookmark}
          onScrollToReviews={null} // Убираем ревью для standalone уроков
          onOpenMaterials={handleOpenMaterials}
          onCloseMaterials={handleCloseMaterials}
          materialsModalOpen={materialsModalOpen}
          showHero={true}
          showVideo={true}
          showContent={true}
          showActions={false} // Скрываем actions для standalone урока
          showForum={true} 
          showNavigation={false} // Скрываем навигацию для standalone урока
          showNotes={true} // Показываем заметки
          showMaterials={true}
        />
      )}
      <Menu
        anchorEl={testMenuAnchor}
        open={Boolean(testMenuAnchor)}
        onClose={() => setTestMenuAnchor(null)}
      >
        {testMenuItems.map((testId, idx) => (
          <MenuItem key={testId._id || testId} onClick={() => {
            navigate(`/admin/tests/${testId._id || testId}`);
            setTestMenuAnchor(null);
          }}>
            {t('tests:view.testNumber', { number: idx + 1 })}
          </MenuItem>
        ))}
      </Menu>
    </Container>
  );
};

export default LessonPage; 